import { addDays, endOfDay, isAfter, startOfDay } from 'date-fns';
import { useMemo } from 'react';
import { PlannedOccurrence } from 'shared/types/habit-schedule';
import { Task } from 'shared/types/task';
import { DueDisplayOptions } from 'shared/types/task-base';
import { Timestamp } from 'shared/types/timestamp';
import { WeekDays } from 'shared/types/week-days';
import { getActiveSchedule } from 'shared/utils/get-active-schedule';
import { getTaskPlanning } from 'shared/utils/get-task-planning';
import { getScheduleTimeframeByDate } from 'shared/utils/schedules';

export type Options = {
  plannedOccurrence?: PlannedOccurrence;
  displayDue: DueDisplayOptions[];
  today: Timestamp;
  displayDate?: Timestamp;
  weekStartsOn: WeekDays;
};

export const useFutureOccurrence = (
  task: Task,
  { plannedOccurrence, displayDue, today, displayDate, weekStartsOn }: Options,
) =>
  useMemo(() => {
    if (plannedOccurrence || !displayDue.includes(DueDisplayOptions.Future)) {
      return undefined;
    }
    const dateToMeasureFrom = endOfDay(displayDate ?? today);
    // normal task
    if (
      task.endStrategy?.deadline &&
      isAfter(task.endStrategy.deadline, endOfDay(dateToMeasureFrom))
    ) {
      return getTaskPlanning(task, {
        startDate: startOfDay(dateToMeasureFrom),
        endDate: dateToMeasureFrom,
        today,
        weekStartsOn,
      })[0];
    }

    // habit/repeating task
    const activeSchedule = getActiveSchedule(task.schedules);
    if (!activeSchedule) {
      return undefined;
    }

    // get the next occurrence in the current timeframe (if it exists)
    const timeframe = getScheduleTimeframeByDate(
      activeSchedule,
      dateToMeasureFrom,
      weekStartsOn,
    );
    const planning = getTaskPlanning(task, {
      startDate: dateToMeasureFrom,
      endDate: timeframe.endDate,
      today,
      weekStartsOn,
      uncompletedOnly: true,
    });
    const futureOccurrence = planning.find(({ date }) =>
      isAfter(date, dateToMeasureFrom),
    );
    if (futureOccurrence) {
      return futureOccurrence;
    }

    // if there is no next occurrence in the current timeframe, get the first occurrence in the next timeframe
    const nextTimeframe = getScheduleTimeframeByDate(
      activeSchedule,
      addDays(timeframe.endDate, 1),
      weekStartsOn,
    );
    const nextPlanning = getTaskPlanning(task, {
      startDate: nextTimeframe.startDate,
      endDate: nextTimeframe.endDate,
      today,
      weekStartsOn,
      uncompletedOnly: true,
    });
    return nextPlanning.find(
      ({ date }) =>
        date.getTime() === nextTimeframe.startDate.getTime() ||
        isAfter(date, nextTimeframe.startDate),
    );
  }, [displayDate, displayDue, plannedOccurrence, task, today, weekStartsOn]);
